import dayjs from "dayjs"
import humps from "humps"
import { UAParser } from "ua-parser-js"

import { GlobalSnackbarRegion } from "@doktor-se/bones-ui"
import {
  AssignedConversation,
  CallStateChangedMessage,
  Conversation,
  ConversationReminderMessage,
  MetaData,
  SeenConversation
} from "@doktor-se/bones-ui/dist/web-shared/types"
import { setLastMessage } from "@doktor-se/bones-ui/dist/web-shared/utils"

import { connectWs, fetchCallToken, retriesWS } from "api"
import { handleErrors } from "api/error/handler"
import handleEvents from "api/ws/handler"
import { addPostponeTimer } from "api/ws/handlers/handleAssignment"
import { datadogConfig, featureFlags } from "config"
import { apiFetch } from "lib/fetch"
import { Thunk } from "lib/hooks"
import { removeUnassignedConversationsFromLocalStorage } from "lib/seald"
import {
  addMessage,
  BookedAppointment,
  clearReassignment,
  loadBookedAppointmentRequests,
  loadBookedAppointments,
  loadConversation,
  loadConversations,
  loadReassignRequests,
  ReassignRequestConversation,
  updateConversationMetadata
} from "reducers/conversations/conversations.reducer"
import { updateJournalDraft, updateJournalDrafts } from "reducers/selected"
import { updateCall } from "reducers/tokbox"
import "types"

const resetCall =
  (conversations: AssignedConversation[]): Thunk =>
  async (dispatch, getState) => {
    const { user } = getState().auth
    const conversationWithCall = conversations.find(
      c => c.assignedStaffId === user?.id && ["active", "outgoing"].includes(c.callState)
    )

    if (conversationWithCall) {
      const conversation = await dispatch(fetchAssignedConversation(conversationWithCall.id))
      if (conversation) {
        const callMessages = conversation?.messages?.filter(
          m => m.type === "call" && ["incoming", "outgoing"].includes((m as CallStateChangedMessage).data.callState)
        ) as CallStateChangedMessage[]
        const sessionId = callMessages[callMessages.length - 1].data.sessionId
        dispatch(fetchCallToken(conversation, sessionId)).then(() =>
          dispatch(updateCall({ callState: conversation.callState, conversationId: conversation.id }))
        )
      }
    } else {
      dispatch(updateCall({ callState: "inactive" }))
    }
  }

interface AssignedConversationsListResponse {
  conversations: AssignedConversation[]
  paginationCount: number
}

export const conversationsConnect = (): Thunk<Promise<true | void>> => async (dispatch, getState) => {
  try {
    const response: AssignedConversationsListResponse = await dispatch(
      apiFetch({
        url: featureFlags.has("conversations_load_split")
          ? `/v3/conversations/assigned/list`
          : `/v2/conversations/assigned/list`
      })
    )
    await dispatch(connectWs(response.conversations, getState().auth.accessToken!))
    return true
  } catch (error: any) {
    if (error.status !== 401) {
      setTimeout(
        () =>
          dispatch(
            handleEvents({
              event: "socket.reconnect",
              retry: retriesWS()
            })
          ),
        3000
      )
    }
    dispatch(handleErrors({ error, customError: true }))
  }
}

const parseAssignedConversation = (conversation: any): AssignedConversation => {
  const parsedMessages = conversation.messages && [
    ...conversation.messages?.map((m: any) => ({
      ...m,
      data: m.data && typeof m.data !== "string" ? { ...m.data } : JSON.parse(m.data)
    }))
  ]

  // Extract platforms from platform_log messages
  const platforms = parsedMessages?.filter((m: any) => m.type === "platform_log").map((p: any) => p.data)

  return humps.camelizeKeys({
    ...conversation,
    history: conversation.history && [
      ...conversation.history.map((h: any) => ({
        ...h,
        journalNote: h.journalNote && {
          ...h.journalNote,
          data: JSON.parse(h.journalNote.data)
        }
      }))
    ],
    messages: parsedMessages,
    lastMessage: setLastMessage(parsedMessages || [], conversation.patientId),
    platforms: platforms || []
  }) as AssignedConversation
}

const parseConversation = (conversation: any, seen?: any): Conversation => {
  const conv = parseAssignedConversation(conversation) as Conversation
  if (seen && seen.length) {
    conv.seen = humps.camelizeKeys([
      ...seen.map((s: any) => ({
        ...s
      }))
    ]) as SeenConversation[]
  }
  return conv
}

export const fetchAssignedConversations = (): Thunk => async (dispatch, getState) => {
  try {
    const response: AssignedConversationsListResponse = await dispatch(
      apiFetch({
        url: featureFlags.has("conversations_load_split")
          ? `/v3/conversations/assigned/list`
          : `/v2/conversations/assigned/list`,
        incomingOptions: {
          loader: "ASSIGNED_CONVERSATIONS"
        }
      })
    )
    let conversations: AssignedConversation[] = response.conversations.map(parseAssignedConversation)
    dispatch(loadConversations(conversations))

    conversations.forEach(conversation => {
      if (
        conversation.messages &&
        !conversation.messages.some(
          message => message.type === "staff_platform_log" && message.createdById === getState().auth.user?.id
        )
      ) {
        dispatch(platformLog(conversation.id))
      }
    })

    if (featureFlags.has("journal")) dispatch(updateJournalDrafts(conversations))

    if (featureFlags.has("e2ee")) {
      removeUnassignedConversationsFromLocalStorage(conversations)
    }

    conversations.forEach(conversation => {
      if (!!conversation.postponedUntil && dayjs(conversation.postponedUntil) > dayjs()) {
        dispatch(addPostponeTimer(conversation.id, conversation.postponedUntil))
      }
    })

    dispatch(resetCall(conversations))
  } catch (error: any) {
    dispatch(handleErrors({ error }))
  }
}

export const fetchAssignedConversation =
  (conversationId: string): Thunk<Promise<void | AssignedConversation>> =>
  async (dispatch, getState) => {
    try {
      const response = await dispatch(apiFetch(`/conversations/assigned/${conversationId}`))
      let conversation = parseAssignedConversation(response)
      const user = getState().auth.user
      if (!user) return
      dispatch(loadConversation({ conversation, user }))
      if (featureFlags.has("journal")) {
        dispatch(updateJournalDraft(conversation))
      }
      if (
        conversation.assignedStaffId === user.id &&
        !conversation.messages.some(message => message.type === "staff_platform_log" && message.createdById === user.id)
      ) {
        dispatch(platformLog(conversation.id))
      }
      return conversation
    } catch (error: any) {
      dispatch(handleErrors({ error, customContext: "/conversations/assigned/<conversationId>" }))
    }
  }

export const fetchConversationById =
  (conversationId: string): Thunk<Promise<void | Conversation>> =>
  async (dispatch, getState) => {
    try {
      const response = await dispatch(apiFetch(`/v2/conversations/${conversationId}`))
      const conversation = parseConversation(response.conversation, response.seen)
      const user = getState().auth.user
      if (!user) return
      dispatch(loadConversation({ conversation: conversation as AssignedConversation, user }))
      if (featureFlags.has("journal")) {
        dispatch(updateJournalDraft(conversation as AssignedConversation))
      }
      if (
        conversation.assignedStaffId === user.id &&
        !conversation.messages.some(message => message.type === "staff_platform_log" && message.createdById === user.id)
      ) {
        dispatch(platformLog(conversation.id))
      }
      return conversation
    } catch (error: any) {
      dispatch(handleErrors({ error, customContext: "/conversations/<conversationId>" }))
    }
  }

export const platformLog =
  (conversationId: string): Thunk =>
  dispatch => {
    const result = new UAParser().setUA(navigator.userAgent).getResult()
    dispatch(
      apiFetch(`/conversations/${conversationId}/platform`, {
        method: "POST",
        body: JSON.stringify({
          type: "carealot",
          manufacturer: result.device.vendor,
          os: `${result.os.name} ${result.os.version}`,
          app_version: datadogConfig.version,
          device_type: result.device.type,
          browser: `${result.browser.name} ${result.browser.version}`
        })
      })
    ).catch((error: any) => {
      dispatch(handleErrors({ error, customError: true, customContext: "/conversations/<conversationId>/platform" }))
    })
  }

export const setMetadataOnConversation = (conversationId: string, metadata: MetaData): Thunk => {
  const { clinic, closingStaffRole, paymentRequired, isUpcomingBooking, preventReopen } = metadata
  return async dispatch => {
    try {
      const response: boolean = await dispatch(
        apiFetch(`/conversations/${conversationId}/metadata`, {
          method: "POST",
          body: JSON.stringify({
            clinic,
            closing_staff_role: closingStaffRole,
            payment_required: paymentRequired,
            is_upcoming_booking: isUpcomingBooking,
            prevent_reopen: preventReopen
          })
        })
      )
      if (response)
        dispatch(
          updateConversationMetadata({
            conversationId,
            metadata: { clinic, closingStaffRole, paymentRequired, isUpcomingBooking, preventReopen }
          })
        )
    } catch (error: any) {
      dispatch(handleErrors({ error, customContext: "/conversations/<conversationId>/metadata" }))
    }
  }
}

export interface PostponeTimesResponse {
  times: string[]
  showAsap: boolean
}
export const getPostponeTimes = (
  conversationId: string,
  queueName: string
): Thunk<Promise<PostponeTimesResponse | undefined>> => {
  return async dispatch => {
    try {
      return await dispatch(apiFetch(`/conversations/${conversationId}/postpone_times?queue_key=${queueName}`))
    } catch (error: any) {
      dispatch(handleErrors({ error, customContext: "/conversations/<conversationId>/postpone_times" }))
    }
  }
}

export const sendSmsReminder = (conversationId: string): Thunk<Promise<undefined>> => {
  return async dispatch => {
    try {
      const response = await dispatch(
        apiFetch(`/conversations/${conversationId}/send_sms_reminder`, { method: "POST" })
      )
      dispatch(addMessage(response as ConversationReminderMessage))
    } catch (error: any) {
      dispatch(handleErrors({ error, customContext: "/conversations/<conversationId>/send_sms_reminder" }))
    }
  }
}

export const fetchClinicConversations = (enableLoader = true): Thunk<Promise<void>> => {
  return async dispatch => {
    try {
      const response: { conversations: Array<{ conversation: BookedAppointment }> } = await dispatch(
        apiFetch({
          url: "/conversations/clinic",
          ...(enableLoader && {
            incomingOptions: {
              loader: "CLINIC_CONVERSATIONS"
            }
          })
        })
      )
      const mapped = response.conversations.map(({ conversation }) => conversation)
      // we select only those that are not reassign requests
      const booked = mapped.filter(c => c.reassignTo == null)
      dispatch(loadBookedAppointments(booked))
      // we select only those that are waiting for approval
      const requests = mapped.filter(c => !!c.reassignTo)
      dispatch(loadBookedAppointmentRequests(requests))
    } catch (error: any) {
      dispatch(handleErrors({ error, customContext: "/conversations/clinic" }))
    }
  }
}

export const fetchReassignedConversations = (): Thunk<Promise<void>> => {
  return async dispatch => {
    try {
      const response: { conversations: Array<{ conversation: ReassignRequestConversation }> } = await dispatch(
        apiFetch({
          url: "/conversations/reassigned"
        })
      )
      const mapped = response.conversations.map(({ conversation }) => conversation)
      dispatch(loadReassignRequests(mapped))
    } catch (error: any) {
      dispatch(handleErrors({ error, customContext: "/conversations/reassigned" }))
    }
  }
}

export const sendRequestDecision = (
  conversationId: string,
  approvalDecision: boolean,
  toastProps?: { showToast: boolean; text: string }
): Thunk<Promise<void>> => {
  return async dispatch => {
    try {
      await dispatch(
        apiFetch(`/conversations/${conversationId}/approve_reassignment`, {
          method: "POST",
          body: JSON.stringify({ is_reassignment_approved: approvalDecision })
        })
      )
      if (toastProps && toastProps.showToast) {
        GlobalSnackbarRegion.queue.add(
          {
            text: toastProps.text,
            variant: "success"
          },
          { timeout: 4000 }
        )
      }
    } catch (error: any) {
      dispatch(handleErrors({ error, customContext: "/conversations/<conversationId>/approve_reassignment" }))
    }
  }
}

export const fetchRequestConversation =
  (conversationId: string): Thunk<Promise<ReassignRequestConversation | void>> =>
  async dispatch => {
    try {
      const response = await dispatch(apiFetch(`/conversations/assigned/${conversationId}`))
      return response
    } catch (error: any) {
      dispatch(handleErrors({ error, customContext: "/conversations/assigned/<conversationId>" }))
    }
  }

export const clearRejectedReassignment =
  (conversationId: string): Thunk<void> =>
  async dispatch => {
    try {
      await dispatch(apiFetch(`/conversations/${conversationId}/clear_reassignment`, { method: "POST" }))
      await dispatch(clearReassignment(conversationId))
    } catch (error: any) {
      dispatch(handleErrors({ error, customContext: "/conversations/<conversationId>/clear_reassignment" }))
    }
  }

export const updateConversationCategory =
  (conversationId: string, categoryName: string): Thunk<Promise<boolean>> =>
  async dispatch => {
    try {
      await dispatch(
        apiFetch(`/conversations/${conversationId}/update_category`, {
          method: "PUT",
          body: JSON.stringify({ category: categoryName })
        })
      )
      return true
    } catch (error: any) {
      dispatch(handleErrors({ error, customContext: "/conversations/<conversationId>/update_category" }))
      return false
    }
  }

export const sendBookingRequestDecision = (
  bookingId: string,
  approvalDecision: boolean,
  toastProps?: { showToast: boolean; text: string }
): Thunk<Promise<void>> => {
  return async dispatch => {
    try {
      await dispatch(
        apiFetch(`/calendar/bookings/${bookingId}/approve_reassignment`, {
          method: "POST",
          body: JSON.stringify({ is_reassignment_approved: approvalDecision })
        })
      )
      if (toastProps && toastProps.showToast) {
        GlobalSnackbarRegion.queue.add(
          {
            text: toastProps.text,
            variant: "success"
          },
          { timeout: 4000 }
        )
      }
    } catch (error: any) {
      dispatch(handleErrors({ error, customContext: "/calendar/bookings/<bookingId>/approve_reassignment" }))
    }
  }
}

export const conversationSeen = (conversationId: string, messageId: number): Thunk<Promise<void>> => {
  return async dispatch => {
    try {
      await dispatch(
        apiFetch(`/conversations/${conversationId}/messages/${messageId}/seen`, {
          method: "POST"
        })
      )
    } catch (error: any) {
      dispatch(
        handleErrors({
          error,
          customContext: "/conversations/<conversationId>/messages/<messageId>/seen"
        })
      )
    }
  }
}

export const setMinimumRequiredCareLevel = (conversationId: string, careLevel: string): Thunk<Promise<void>> => {
  return async dispatch => {
    try {
      await dispatch(
        apiFetch(`/conversations/${conversationId}`, {
          method: "PATCH",
          body: JSON.stringify({
            minimum_required_care_level: careLevel
          })
        })
      )
    } catch (error: any) {
      dispatch(
        handleErrors({
          error,
          customContext: "/conversations/<conversationId>"
        })
      )
    }
  }
}
